"use client"

import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation" 
import { Menu, X, LayoutDashboard } from "lucide-react" 
import { useState, useEffect, useRef } from "react"
import { Manrope } from "next/font/google"
import { NavbarCTAButton } from "../button/CTAButton"
import { useLocale } from "@/context/LocaleContext"
import LanguageToggleButton from "@/components/header/LanguageToggleButton"
import Cookies from "js-cookie"
import { getUserSession } from "@/lib/session"

interface FiturMenuItem {
  name: string;
  desc: string;
  href: string;
  icon: string;
}

interface MenuItem {
  name: string;
  href?: string;
  isMegaMenu?: boolean;
  megaMenuContent?: FiturMenuItem[];
}

const manrope = Manrope({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700", "800"],
})

export default function Navbar() {
  const pathname = usePathname() || "" 
  const { t } = useLocale(); 
  
  const isEnglish = pathname.startsWith("/en");

  const getHref = (path: string) => {
    if (isEnglish) {
        return path === "/" ? "/en" : `/en${path}`;
    }
    return path; 
  };

  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false) 
  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const checkLoginStatus = () => {
      const authToken = Cookies.get("auth_token");
      const userSession = getUserSession();
    
      if (userSession || authToken) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
    };

    checkLoginStatus();

    window.addEventListener("session-changed", checkLoginStatus);
    window.addEventListener("storage", checkLoginStatus);

    return () => {
      window.removeEventListener("session-changed", checkLoginStatus);
      window.removeEventListener("storage", checkLoginStatus);
    };
  }, [pathname]);

  const fiturItems = [
    { 
      name: t("LandingPage.fitur_1_title") || "All-in-One Undangan Digital Platform", 
      desc: t("LandingPage.fitur_1_desc") || "Semua fitur dasar undangan digital tersedia lengkap di Elynk.co. Gratis digunakan, bisa disebar...", 
      href: getHref("/features/all-in-one-invitation"), 
      icon: "/images/icons/all-in-one.svg" 
    },
    { 
      name: t("LandingPage.fitur_2_title") || "Sebar Otomatis via WhatsApp", 
      desc: t("LandingPage.fitur_2_desc") || "Sebarkan undangan digital ke seluruh tamu...", 
      href: getHref("/features/whatsapp-broadcast"), 
      icon: "/images/icons/call.svg" 
    },
    { 
      name: t("LandingPage.fitur_3_title") || "QR Code Check-in Buku Tamu", 
      desc: t("LandingPage.fitur_3_desc") || "Setiap tamu mendapatkan QR Code unik...", 
      href: getHref("/features/qr-check-in"), 
      icon: "/images/icons/qr-code-icon.svg" 
    },
    { 
      name: t("LandingPage.fitur_4_title") || "Amplop Digital Otomatis", 
      desc: t("LandingPage.fitur_4_desc") || "Elynk menyediakan sistem amplop digital...", 
      href: getHref("/features/digital-envelope"), 
      icon: "/images/icons/adg.svg" 
    },
    { 
      name: t("LandingPage.fitur_5_title") || "Fleksibel Custom RSVP", 
      desc: t("LandingPage.fitur_5_desc") || "Kelola konfirmasi kehadiran tamu...", 
      href: getHref("/features/custom-rsvp"), 
      icon: "/images/icons/rsvp.svg" 
    },
    { 
      name: t("LandingPage.fitur_6_title") || "Reminder Acara Otomatis", 
      desc: t("LandingPage.fitur_6_desc") || "Reminder Acara Otomatis di Elynk.co memastikan...", 
      href: getHref("/features/event-reminders"), 
      icon: "/images/icons/reminder.svg" 
    },
    { 
      name: t("LandingPage.fitur_7_title") || "Split Acara Setiap Tamu", 
      desc: t("LandingPage.fitur_7_desc") || "Dalam satu undangan, kamu bisa membuat...", 
      href: getHref("/features/multi-event-access"), 
      icon: "/images/icons/split-tamu.svg"
    },
    { 
      name: t("LandingPage.fitur_8_title") || "Titip Kado Otomatis", 
      desc: t("LandingPage.fitur_8_desc") || "Tamu dapat memilih dan membeli kado...", 
      href: getHref("/features/gift-registry"), 
      icon: "/images/icons/titip-kado.svg" 
    },
  ]

  //  Menu dinamis: Dashboard dihapus dari sini agar tidak muncul di tengah
  const menuItems = [
    { name: t("LandingPage.menu_home") || "Home", href: getHref("/") },
    {
      name: t("LandingPage.menu_fitur") || "Fitur",
      isMegaMenu: true,
      megaMenuContent: fiturItems,
    },
    { name: t("LandingPage.menu_harga") || "Harga", href: getHref("/price") },
    {
      name: t("LandingPage.menu_tema") || "Tema",
      href: getHref("/templates"), 
    },
    // {
    //   name: t("LandingPage.menu_vendor") || "Vendor",
    //   href: getHref("/vendor"), 
    // },
    ...(isLoggedIn
      ? [] 
      : [
          { name: t("LandingPage.menu_login") || "Login", href: getHref("/login") },
          { name: t("LandingPage.menu_register") || "Register", href: getHref("/register") },
        ]),
  ]

  const handleToggle = (menu: string) => {
    if (openMenu === menu) {
      setOpenMenu(null);
    } else {
      setOpenMenu(menu);
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setOpenMenu(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const activeMegaMenu = menuItems.find(item => item.name === openMenu && item.isMegaMenu)

  const isParentActive = (item: MenuItem) => {
    const cleanPath = pathname.replace(/^\/en/, "") || "/";

    if (item.href) {
      const itemPath = item.href.replace(/^\/en/, "") || "/";
      if (itemPath === "/") return cleanPath === "/"; 
      return cleanPath.startsWith(itemPath); 
    }
    
    if (item.isMegaMenu && item.megaMenuContent) {
      return item.megaMenuContent.some((c: FiturMenuItem) => {
        const cleanChildPath = c.href.replace(/^\/en/, "") || "/";
        return cleanPath === cleanChildPath;
      });
    }

    return false;
  };

  const handleCTAClick = () => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("preSelectedPackage");
    }
  };

  const hiddenRoutes = ['/privacy-policy', '/terms-of-service'];
  if (hiddenRoutes.includes(pathname)) {
    return null;
  }

  return (
    <header
      ref={headerRef}
      className={`${manrope.className} fixed left-0 right-0 z-50 w-full flex justify-center transition-all duration-300 ease-in-out ${
        isScrolled ? "top-0 px-0" : "px-4 top-3 md:top-4" 
      }`}
    >
      <nav 
        className={`relative w-full transition-all duration-300 ease-in-out ${
          isScrolled ? "max-w-none" : "max-w-[1300px]"
        }`}
      >
        <div 
          className={`relative z-40 flex items-center justify-between w-full h-[70px] bg-white/95 backdrop-blur-md transition-all duration-300 ease-in-out
            ${isScrolled ? "rounded-none border-b border-gray-100" : "rounded-[19px] border border-gray-100"}
            ${openMenu ? "shadow-[0_15px_40px_rgba(0,0,0,0.12)]" : (isScrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)]" : "shadow-[0_8px_25px_rgba(0,0,0,0.06)]")}
          `}
        >
          <div className={`flex items-center justify-between w-full h-full mx-auto transition-all duration-300 ease-in-out ${
              isScrolled ? "max-w-[1300px] px-4 md:px-8" : "max-w-full px-4 md:px-8" 
          }`}>
              <Link href={getHref("/")} className="flex items-center gap-2 flex-shrink-0 mr-4 xl:mr-8">
                <Image
                  src="/images/logo/elynk.png"
                  alt="Elynk Logo"
                  width={170}
                  height={70}
                  className={`w-auto h-10 sm:h-12 object-contain transition-all duration-300 ease-in-out ${isScrolled ? "md:-ml-9" : "md:-ml-4"}`}
                />
              </Link>
              
              <ul className="hidden xl:flex items-center gap-7 text-[15px] flex-1 justify-center font-normal">
                {menuItems.map((item) => {
                  const hasDropdown = item.isMegaMenu;
                  const isActive = isParentActive(item); 
                  
                  return (
                    <li key={item.name} className="relative group">
                      {hasDropdown ? (
                        <button
                          onClick={() => handleToggle(item.name)}
                          className={`flex items-center gap-1.5 py-2 transition-colors duration-200 outline-none
                            ${isActive || openMenu === item.name ? "text-[#E85C57] font-semibold" : "text-gray-700 hover:text-[#E85C57]"}`}
                        >
                          {item.name}
                          <span
                            className={`w-2.5 h-2.5 transition-transform duration-300 mt-[2px] inline-block ${
                              openMenu === item.name ? "rotate-180" : "rotate-0" 
                            } ${
                              isActive || openMenu === item.name ? "bg-[#E85C57]" : "bg-gray-900 group-hover:bg-[#E85C57]"
                            }`}
                            style={{
                              WebkitMask: "url('/images/icons/arrow-down.svg') center/contain no-repeat",
                              mask: "url('/images/icons/arrow-down.svg') center/contain no-repeat",
                            }}
                          />
                        </button>
                      ) : (
                        <Link
                          href={item.href!}
                          className={`py-2 transition-colors duration-200 block
                            ${isActive ? "text-[#E85C57] font-semibold" : "text-gray-700 hover:text-[#E85C57]"}`}
                        >
                          {item.name}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>

              <div className="flex items-center gap-2 md:gap-3">
                {isLoggedIn && (
                  <Link 
                    href={getHref("/admin")} 
                    className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-[#E85C57] transition-all font-medium text-[14px]"
                    title="To Dashboard"
                  >
                    <LayoutDashboard className="w-[18px] h-[18px]" />
                    <span>Dashboard</span>
                  </Link>
                )}
                
                <div className="hidden md:block" onClick={handleCTAClick}>
                  <NavbarCTAButton />
                </div>
                <div className="hidden md:block z-50">
                  <LanguageToggleButton direction="down" showLabel={false} />
                </div>

                <button
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="xl:hidden text-gray-700 hover:text-[#E85C57] transition-colors focus:outline-none p-2 rounded-full hover:bg-gray-50 mr-1"
                  aria-label="Toggle menu"
                >
                  {mobileOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
              </div>

          </div>
        </div>

        {activeMegaMenu && (
          <div 
            className={`hidden xl:block absolute top-full z-30 -mt-3 pt-3 cursor-default animate-in fade-in slide-in-from-top-2 transition-all duration-300 ease-in-out
              ${isScrolled 
                ? "left-1/2 -translate-x-1/2 w-full max-w-[1300px] px-4 md:px-8" 
                : "left-0 right-0 w-full"
              }
            `} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`bg-white border border-gray-100 border-t-0 shadow-[0_30px_60px_rgba(0,0,0,0.08)] p-8 pt-7 pb-10 transition-all duration-300 ease-in-out
              ${isScrolled 
                ? "rounded-b-[24px] md:-ml-9 md:w-[calc(100%+2.25rem)]" 
                : "rounded-t-[20px] rounded-b-[24px]"
              }
            `}>
              
              {activeMegaMenu.name === (t("LandingPage.menu_fitur") || "Fitur") && (
                <h3 className="text-[1.2rem] font-bold text-[#2A2A2A] mb-6 text-left ml-2">{t("LandingPage.fitur_layanan") || "Fitur Layanan"}</h3>
              )}
              
              <div className="grid gap-x-6 gap-y-6 text-left grid-cols-4">
                {activeMegaMenu.megaMenuContent?.map((item, idx) => {
                  const isChildActive = pathname === item.href; 
                  return (
                    <Link 
                      href={item.href} 
                      key={idx} 
                      className={`group flex gap-4 items-start p-3 -m-3 rounded-xl transition-colors ${
                        isChildActive ? "bg-[#fff5f4]" : "hover:bg-gray-50"
                      }`} 
                      onClick={() => setOpenMenu(null)}
                    >
                      <div className="shrink-0 mt-0.5">
                        <div 
                          className="bg-[#E85C57] group-hover:scale-110 transition-transform duration-300 w-[34px] h-[34px]"
                          style={{
                            WebkitMaskImage: `url('${item.icon}')`,
                            WebkitMaskSize: "contain",
                            WebkitMaskRepeat: "no-repeat",
                            WebkitMaskPosition: "center",
                            maskImage: `url('${item.icon}')`,
                            maskSize: "contain",
                            maskRepeat: "no-repeat",
                            maskPosition: "center",
                          }}
                        />
                      </div>
                      <div>
                        <h4 className={`text-[1.05rem] font-bold mb-1.5 leading-snug transition-colors ${
                          isChildActive ? "text-[#E85C57]" : "text-[#2A2A2A] group-hover:text-[#E85C57]"
                        }`}>
                          {item.name}
                        </h4>
                        <p className={`text-[0.85rem] leading-relaxed line-clamp-3 ${
                          isChildActive ? "text-gray-600" : "text-gray-500"
                        }`}>
                          {item.desc}
                        </p>
                      </div>
                    </Link>
                  )
                })}
              </div>
              
            </div>
          </div>
        )}
      </nav>

      {mobileOpen && (
        <div
          className={`absolute left-4 right-4 z-40 
               bg-white border border-gray-100 shadow-[0_20px_40px_rgba(0,0,0,0.1)] 
               rounded-3xl overflow-hidden max-h-[85vh] overflow-y-auto xl:hidden 
               animate-in slide-in-from-top-4 fade-in no-scrollbar
               ${isScrolled ? "top-[75px]" : "top-[85px]"}`}
        >
          <ul className="flex flex-col gap-1.5 text-[15px] font-medium p-4">
            {menuItems.map((item) => {
              const hasDropdown = item.isMegaMenu;
              const isActive = isParentActive(item); 

              return (
                <li key={item.name}>
                  {hasDropdown ? (
                    <div>
                      <button
                        onClick={() => handleToggle(item.name)}
                        className={`flex items-center justify-between w-full py-4 px-4 rounded-xl transition-colors group
                          ${isActive || openMenu === item.name ? "bg-[#fff5f4] text-[#E85C57] font-semibold" : "text-gray-700 hover:bg-gray-50 hover:text-[#E85C57]"}`}
                      >
                        {item.name}
                        <span
                          className={`w-3 h-3 transition-transform duration-300 inline-block ${
                            openMenu === item.name ? "rotate-180" : "rotate-0"
                          } ${
                            isActive || openMenu === item.name ? "bg-[#E85C57]" : "bg-gray-900 group-hover:bg-[#E85C57]"
                          }`}
                          style={{
                            WebkitMask: "url('/images/icons/arrow-down.svg') center/contain no-repeat",
                            mask: "url('/images/icons/arrow-down.svg') center/contain no-repeat",
                          }}
                        />
                      </button>                      
                      {openMenu === item.name && item.isMegaMenu && (
                        <div className="pl-2 pr-2 py-3 pb-4 flex flex-col gap-3 border-l-2 border-gray-100 ml-6 my-2">
                          {item.megaMenuContent?.map((megaItem, idx) => {
                            const isChildActive = pathname === megaItem.href;
                            return (
                              <Link 
                                href={megaItem.href} 
                                key={idx} 
                                className={`group flex gap-3 items-start p-2.5 rounded-xl transition-colors ${
                                  isChildActive ? "bg-[#fff5f4]" : "hover:bg-gray-50"
                                }`} 
                                onClick={() => setMobileOpen(false)}
                              >
                                <div className="shrink-0 p-1.5 rounded-lg bg-[#fff5f4]">
                                  <div 
                                    className="bg-[#E85C57] group-hover:scale-110 transition-transform w-5 h-5" 
                                    style={{
                                      WebkitMaskImage: `url('${megaItem.icon}')`,
                                      WebkitMaskSize: "contain",
                                      WebkitMaskRepeat: "no-repeat",
                                      WebkitMaskPosition: "center",
                                      maskImage: `url('${megaItem.icon}')`,
                                      maskSize: "contain",
                                      maskRepeat: "no-repeat",
                                      maskPosition: "center",
                                    }}
                                  />
                                </div>
                                <div>
                                  <h4 className={`text-[13px] font-bold leading-tight mb-1 transition-colors ${
                                    isChildActive ? "text-[#E85C57]" : "text-[#2A2A2A] group-hover:text-[#E85C57]"
                                  }`}>
                                    {megaItem.name}
                                  </h4>
                                  <p className="text-[12px] text-gray-500 line-clamp-2 leading-relaxed">{megaItem.desc}</p>
                                </div>
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item.href!}
                      className={`block py-4 px-4 rounded-xl transition-colors
                        ${isActive ? "text-[#E85C57] bg-[#fff5f4] font-semibold" : "text-gray-700 hover:bg-gray-50 hover:text-[#E85C57]"}`}
                      onClick={() => setMobileOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              );
            })}

            {/*  Tampilan khusus Dashboard untuk Mobile Menu */}
            <div className="md:hidden pt-6 pb-2 px-4 mt-2 border-t border-gray-100 flex flex-col gap-3 justify-center md:-mr-5">
              {isLoggedIn && (
                <Link 
                  href={getHref("/admin")} 
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 hover:text-[#E85C57] hover:border-[#E85C57]/30 font-semibold text-[14px] transition-all"
                  onClick={() => setMobileOpen(false)}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Dashboard
                </Link>
              )}
              <LanguageToggleButton fullWidth={true} direction="up" showLabel={true} />
              <div onClick={handleCTAClick}>
                <NavbarCTAButton className="w-full" />
              </div>
            </div>
          </ul>
        </div>
      )}
    </header>
  )
}